import { useEffect, useRef, useState } from "react";
import { ExternalLink } from "lucide-react";
import { AFFILIATE_URL } from "../data";
import { t } from "../i18n";
import { trackEvent } from "../lib/analytics";

const WIDGET_SRC = "https://www.discovercars.com/widget.js?v1";

// Official DiscoverCars affiliate snippet. Only officially supported display
// options are customised (colours + localisable texts). Affiliate attribution
// (a_aid via data-aff-code) is untouched; data-aff-channel carries the
// per-landing DiscoverCars channel code (chan=home, fiumicino, malpensa…).
const BASE_ATTRS = {
  "data-dev-env": "com",
  "data-location": "",
  "data-currency": "eur",
  "data-utm-source": "boingcars",
  "data-utm-medium": "widget",
  "data-aff-code": "a_aid",
  "data-autocomplete": "on",
  "data-style-submit-bg-color": "#005F73",
  "data-style-submit-font-color": "#ffffff",
  "data-style-form-bg-color": "#ffffff",
  "data-style-form-font-color": "#0A0A0A",
  "data-style-title-color": "#0A0A0A",
  "data-style_rounded_corners": "on",
  "data-localization_currency_box": "on",
  "data-layout_benefits": "on",
  "data-layout_description": "on",
  "data-layout_logo_style": "on dark",
  "data-layout_powered_by": "on",
  "data-layout_style_form_bg_color": "#005F73",
  "data-layout_title": "on",
};

// The official widget renders next to its <script id="dchwidget"> tag. The
// script lives once in a parked container; rendered markup moves between park
// and visible host. Changing language or channel rebuilds the widget.
const state = { loaded: false, script: null, key: null };

function getPark() {
  let park = document.getElementById("dc-park");
  if (!park) {
    park = document.createElement("div");
    park.id = "dc-park";
    park.style.display = "none";
    document.body.appendChild(park);
  }
  return park;
}

function moveContent(from, to) {
  Array.from(from.childNodes).forEach((node) => {
    if (node !== state.script) to.appendChild(node);
  });
}

export default function SearchWidget({ lang = "en", channel = "home" }) {
  const hostRef = useRef(null);
  const [failed, setFailed] = useState(false);
  const tracked = useRef(false);
  const key = `${lang}|${channel}`;

  useEffect(() => {
    // During react-snap's prerender crawl the widget stays out of the snapshot:
    // hydration then mounts it exactly once on the client (no duplicated iframe).
    if (typeof navigator !== "undefined" && navigator.userAgent.includes("ReactSnap")) return;

    const host = hostRef.current;
    if (!host) return;
    const park = getPark();

    if (state.loaded && state.key !== key) {
      park.replaceChildren();
      host.replaceChildren();
      state.script = null;
      state.loaded = false;
    }

    if (!state.loaded) {
      host.replaceChildren();
      state.loaded = true;
      state.key = key;
      const i = t[lang];
      const script = document.createElement("script");
      script.id = "dchwidget";
      script.src = WIDGET_SRC;
      script.async = true;
      const attrs = {
        ...BASE_ATTRS,
        "data-lang": lang,
        "data-aff-channel": channel,
        "data-style-submit-text": i.widget.submitText,
        "data-title-text": i.widget.titleText,
        "data-layout_description_text": i.widget.descText,
      };
      Object.entries(attrs).forEach(([k, v]) => script.setAttribute(k, v));
      script.addEventListener("error", () => setFailed(true), { once: true });
      state.script = script;
      park.appendChild(script);
    }

    let tries = 0;
    const interval = setInterval(() => {
      moveContent(park, host);
      tries += 1;
      if (host.childNodes.length > 0) {
        clearInterval(interval);
      } else if (tries > 40) {
        clearInterval(interval);
        setFailed(true);
      }
    }, 250);

    return () => {
      clearInterval(interval);
      moveContent(host, park);
    };
  }, [lang, channel, key]);

  const onInteract = () => {
    if (tracked.current) return;
    tracked.current = true;
    trackEvent("car_search", {
      page_path: window.location.pathname,
      language: lang,
      channel,
    });
  };

  return (
    <div className="dc-widget-shell" data-testid="dc-widget-shell">
      <div id="dchwidget-host" ref={hostRef} onPointerDownCapture={onInteract} />
      {failed && (
        <div className="rounded-xl border border-border bg-white p-6 text-sm" role="alert">
          <p className="font-medium">
            {lang === "it" ? "La ricerca non si è caricata. " : "The search could not load right now. "}
          </p>
          <a
            href={`${AFFILIATE_URL}&chan=${channel}`}
            target="_blank"
            rel="noopener sponsored"
            data-testid="dc-widget-fallback-link"
            className="mt-3 inline-flex items-center gap-2 font-semibold text-petrol underline-offset-4 transition-colors duration-200 hover:text-petrol-light"
          >
            {lang === "it" ? "Continua la ricerca sul nostro partner di prenotazione" : "Continue the search on our booking partner"}{" "}
            <ExternalLink className="h-4 w-4" />
          </a>
        </div>
      )}
    </div>
  );
}
